<template>
    <form
        data-test-id="frequency-set-form"
        @submit.prevent="createFrequencySet"
    >
        <label for="name">Import Existing Frequency Settings (Optional)</label>
        <div class="form-group">
            <div class="import-frequency-settings">
                <frequency-set-select
                    v-model="selectedFrequencySetId"
                    :frequency-sets="frequencySets"
                    placeholder="Import Existing Frequency Set"
                    test-id="frequency-set-list"
                />
                <button
                    class="btn btn-medium"
                    data-test-id="import-frequency-set-button"
                    :disabled="selectedFrequencySetId === null"
                    @click.prevent="importSelectedFrequencySet()"
                >
                    Import
                </button>
            </div>
        </div>
        <div class="form-group">
            <label for="name">Name</label>
            <input
                id="name"
                v-model="name"
                data-test-id="name"
                :maxlength="maxNameLength"
                required
                type="text"
            />
        </div>

        <div class="form-group">
            <label for="max_power">Max Power</label>
            <select
                id="max_power"
                v-model="max_power"
                data-test-id="max-power"
                required
            >
                <option value="0.5W">0.5W</option>
                <option value="1W">1W</option>
                <option value="2W">2W</option>
                <option value="4W">4W</option>
                <option value="5W">5W</option>
            </select>
        </div>

        <div class="form-group bandwidth-section">
            <label for="bandwidth">Bandwidth</label>
            <select
                id="bandwidth"
                v-model="chosenBandwidthValue"
                data-test-id="bandwidth"
                required
            >
                <option
                    v-for="(choice, index) in bandwidths"
                    :key="`b${index}`"
                    :data-test-id="`bandwidth-choice-${index}`"
                    :value="choice.value"
                >
                    {{ choice.display }}
                </option>
            </select>
        </div>

        <div class="form-group channel-section">
            <h3 class="channel-type-header">Channel</h3>
            <div
                v-for="(channel, index) in channels"
                :key="`c${index}`"
                :class="['channel-field-container']"
            >
                <input
                    v-model="channel.frequency"
                    type="text"
                    :class="['channel-field']"
                    placeholder="Frequency (MHz)"
                    maxlength="9"
                    required
                    :data-test-id="`channel-${index}`"
                    @keyup="enforceDecimal($event)"
                />
            </div>
            <a
                v-if="channels.length < maxAllowedChannels"
                :class="['btn', 'add-channel-btn']"
                href="#"
                data-test-id="add-channel-button"
                @click.prevent="addChannel"
            >
                <span class="icon-add"></span> Add
            </a>
            <a
                v-if="channels.length > 1"
                class="btn remove-channel-btn"
                data-test-id="remove-channel-button"
                href="#"
                @click.prevent="removeChannel"
            >
                <span class="icon-remove"></span> Remove
            </a>
        </div>

        <form-errors :response="errorResponse" />

        <div
            v-if="showSaveAndCancel"
            class="form-group save-and-cancel"
        >
            <button
                :class="['btn', 'btn-secondary']"
                data-test-id="cancel-button"
                @click.prevent="$emit('cancel-button-clicked')"
            >
                <div>Cancel</div>
            </button>
            <button
                :class="['btn', 'btn-primary', { active: submitted }]"
                data-test-id="save-button"
                type="submit"
            >
                <div>Save</div>
            </button>
        </div>
        <div
            v-else
            class="form-group"
        >
            <button
                :class="['btn', 'btn-primary', { active: submitted }]"
                data-test-id="submit-button"
                type="submit"
            >
                <div>Create</div>
            </button>
        </div>
    </form>
</template>

<script>
import FormErrors from '@/components/FormErrors.vue'
import FrequencySetSelect from '@/components/FrequencySetSelect.vue'
import canToast from '@/mixins/canToast'
import enforcesDecimals from '@/mixins/enforcesDecimals'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import frequencySetTypes from '@/constants/frequency-set-types.js'

export default {
    components: {
        FormErrors,
        FrequencySetSelect,
    },

    mixins: [canToast, enforcesDecimals, needsOrganizationSlug],

    props: {
        createEndpoint: {
            type: String,
            required: false,
        },
        showSaveAndCancel: {
            type: Boolean,
            required: false,
            default: false,
        },
        frequencySet: {
            type: Object,
            required: false,
        },
    },

    emits: ['frequency-set-created', 'cancel-button-clicked'],

    data() {
        return {
            name: null,
            max_power: null,
            chosenBandwidthValue: null,
            channels: [
                {
                    frequency: '',
                },
            ],
            errorResponse: null,
            submitted: false,
            frequencySets: [],
            selectedFrequencySetId: null,
            maxNameLength: 20,
            maxAllowedChannels: 1,
        }
    },

    computed: {
        bandwidths() {
            return [
                {
                    display: '12.5kHz',
                    value: '12.5kHz',
                },
                {
                    display: '25kHz',
                    value: '25kHz',
                },
                {
                    display: '50kHz',
                    value: '50kHz',
                },
            ]
        },
    },

    mounted() {
        this.fetchFrequencySets()
        if (this.frequencySet) {
            this.name = this.frequencySet.name
            this.max_power = this.frequencySet.max_power
            this.chosenBandwidthValue = this.frequencySet.bandwidth
            this.channels = this.frequencySet.channels
        }
    },

    methods: {
        createFrequencySet() {
            this.submitted = true
            const hasCustomEndpoint = this.createEndpoint !== undefined
            const endpoint = hasCustomEndpoint
                ? this.createEndpoint
                : `/${this.organizationSlug}/frequency-sets?type=${frequencySetTypes.NEXT_GEN.type}`

            this.$http
                .post(endpoint, {
                    name: this.name,
                    max_power: this.max_power,
                    bandwidth: this.chosenBandwidthValue,
                    channels: this.channels,
                })
                .then((data) => {
                    this.submitted = false
                    this.$emit('frequency-set-created', data.data)
                    if (!hasCustomEndpoint) {
                        this.toast('Frequency set successfully created!')
                        this.$router.push(`/${this.organizationSlug}/frequency-sets`)
                    }
                })
                .catch((error) => {
                    this.submitted = false

                    this.errorResponse = error.response
                })
        },

        addChannel() {
            this.channels.push({
                frequency: '',
            })
        },

        removeChannel() {
            if (this.channels.length <= 1) return

            this.channels.pop()
        },

        fetchFrequencySets() {
            return this.$http
                .get(`/${this.organizationSlug}/frequency-sets/names?type=${frequencySetTypes.NEXT_GEN.type}`)
                .then(({ data }) => {
                    this.frequencySets = data
                })
        },

        importSelectedFrequencySet() {
            this.errorResponse = null
            return this.$http
                .get(`/${this.organizationSlug}/frequency-sets/${this.selectedFrequencySetId}`)
                .then(({ data }) => {
                    this.name = `Copy of ${data.name}`.substring(0, this.maxNameLength)
                    this.max_power = data.max_power
                    this.chosenBandwidthValue = data.bandwidth
                    this.channels = data.channels
                })
                .catch((error) => {
                    this.toast('There was a problem importing the frequency set.', 'error')
                })
        },
    },
}
</script>

<style scoped lang="scss">
@use '@sass/_variables.scss' as *;

.bandwidth-section {
    .section-info {
        margin-top: 12px;
        font-size: 14px;
        line-height: 20px;
        display: flex;

        &:before {
            content: '';
            background-image: url(@images/icon-info.svg);
            background-size: 17px 17px;
            background-repeat: no-repeat;
            background-position: top left;
            width: 17px;
            height: 17px;
            margin-right: 14px;
            display: block;
            flex-shrink: 0;
        }

        &.error {
            color: $red;
            font-family: 'Avenir Heavy';

            &:before {
                background-image: url(@images/icon-alert.svg);
            }
        }
    }
}

.channel-type-header {
    padding-top: 25px;
    border-top: 1px solid #707070;
    margin-bottom: 10px;
    font-family: 'Avenir Heavy';
    font-size: 0.9125em;
}

.channel-type-header:last-of-type {
    margin-top: 25px;
}

.channel-field-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;

    .channel-field {
        margin-bottom: 25px;
    }

    &.error:after {
        content: '';
        background-image: url(@images/icon-alert.svg);
        background-size: 17px 17px;
        background-repeat: no-repeat;
        background-position: top left;
        width: 17px;
        height: 17px;
        display: block;
        flex-shrink: 0;
        position: absolute;
        right: 10px;
        top: 10px;
    }
}

.add-channel-btn,
.remove-channel-btn {
    float: none;
    padding: 0 20px;
    font-size: 0.75em;
    line-height: 30px;

    &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.add-channel-btn .icon-add,
.remove-channel-btn .icon-remove {
    top: 2px;
    font-size: 1rem;
}

.remove-channel-btn {
    margin-left: 5px;
}

.import-frequency-settings {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 10px;

    .p-select {
        width: 275px;
    }
}

.save-and-cancel {
    text-align: right;

    button {
        margin-left: 20px;
    }
}
</style>
